/**
 * Dossier médical personnel — CDC §3.5.
 *
 * Carnet de santé numérique : antécédents personnels et familiaux,
 * traitements, vaccinations, rendez-vous, analyses biologiques et documents.
 * Chaque section est un CRUD générique piloté par une description de champs.
 */
import React, { useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

import { santeApi } from '../api/santeApi'
import { HEALTH_ACCENT, cardSx } from './shared'

/**
 * CRUD générique sur une collection, piloté par `fields`.
 *
 * Un champ peut être : texte/date/nombre, liste déroulante simple
 * (`options`) ou multi-sélection (`options` + `multiple`). `expandPayloads`
 * permet de transformer un formulaire en plusieurs créations (ex. une
 * addiction par substance sélectionnée).
 */
function CrudSection({ api, fields, title, describe, extra, expandPayloads }) {
  const [items, setItems] = useState([])
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({})
  const [loaded, setLoaded] = useState(false)

  const load = async () => {
    setItems(await api.list())
    setLoaded(true)
  }

  const onExpand = (_, expanded) => {
    if (expanded && !loaded) load()
  }

  const save = async () => {
    const payloads = expandPayloads ? expandPayloads(form) : [form]
    for (const payload of payloads) {
      await api.create(payload)
    }
    setForm({})
    setOpen(false)
    load()
  }

  const remove = async (id) => {
    await api.remove(id)
    load()
  }

  return (
    <Accordion
      onChange={onExpand}
      sx={{
        bgcolor: 'transparent',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px !important',
        mb: 1.5,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}>
        <Typography sx={{ color: '#fff', fontWeight: 700 }}>
          {title}
          {loaded && (
            <Typography component="span" sx={{ color: HEALTH_ACCENT, ml: 1 }}>
              ({items.length})
            </Typography>
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          {items.map((it) => (
            <Box
              key={it.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'rgba(255,255,255,0.04)',
                borderRadius: 2,
                px: 2,
                py: 1,
              }}
            >
              <Box>
                <Typography sx={{ color: '#fff', fontSize: 14 }}>
                  {describe(it)}
                </Typography>
                {extra && extra(it, load)}
              </Box>
              <IconButton size="small" onClick={() => remove(it.id)}>
                <DeleteOutlineIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 18 }} />
              </IconButton>
            </Box>
          ))}
          {loaded && items.length === 0 && (
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
              Aucune entrée.
            </Typography>
          )}
          <Button
            size="small"
            onClick={() => setOpen(true)}
            sx={{ color: HEALTH_ACCENT, textTransform: 'none', alignSelf: 'flex-start' }}
          >
            + Ajouter
          </Button>
        </Stack>
      </AccordionDetails>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Ajouter — {title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {fields.map((f) => {
              const isMulti = !!f.options && !!f.multiple
              const value = isMulti
                ? form[f.name] || []
                : form[f.name] || ''
              return (
                <TextField
                  key={f.name}
                  select={!!f.options}
                  type={f.type || 'text'}
                  size="small"
                  label={f.label}
                  helperText={f.helper}
                  value={value}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  InputLabelProps={
                    f.type === 'date' || f.type === 'datetime-local' || isMulti
                      ? { shrink: true }
                      : undefined
                  }
                  SelectProps={
                    isMulti
                      ? {
                          multiple: true,
                          renderValue: (selected) =>
                            (f.options || [])
                              .filter((o) => selected.includes(o.value))
                              .map((o) => o.label)
                              .join(', '),
                        }
                      : undefined
                  }
                  fullWidth
                >
                  {(f.options || []).map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              )
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Annuler</Button>
          <Button
            onClick={save}
            variant="contained"
            sx={{ bgcolor: HEALTH_ACCENT, color: '#00001d' }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  )
}

const CONDITION_TYPES = [
  { value: 'pathology', label: 'Pathologie' },
  { value: 'surgery', label: 'Opération' },
  { value: 'allergy', label: 'Allergie' },
  { value: 'intolerance', label: 'Intolérance' },
]
// §3.5 — addictions suivies (doit refléter ADDICTION_CHOICES côté backend).
const ADDICTIONS = [
  { value: 'tobacco', label: 'Tabac' },
  { value: 'alcohol', label: 'Alcool' },
  { value: 'cannabis', label: 'Cannabis' },
  { value: 'hard_drugs', label: 'Drogues dures' },
  { value: 'medication', label: 'Médicaments' },
  { value: 'sugar', label: 'Sucre' },
  { value: 'caffeine', label: 'Caféine' },
  { value: 'screens', label: 'Écrans' },
  { value: 'social_media', label: 'Réseaux sociaux' },
  { value: 'video_games', label: 'Jeux vidéo' },
  { value: 'gambling', label: "Jeux d'argent" },
  { value: 'shopping', label: 'Achats compulsifs' },
  { value: 'food', label: 'Alimentation compulsive' },
  { value: 'other', label: 'Autre' },
]
const ADDICTION_STATUSES = [
  { value: 'active', label: 'En cours' },
  { value: 'quitting', label: 'Sevrage en cours' },
  { value: 'stopped', label: 'Arrêtée' },
  { value: 'relapsed', label: 'Rechute' },
]
const labelOf = (list, value) =>
  (list.find((o) => o.value === value) || {}).label || value || '—'

const RELATIONS = [
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Frère / sœur' },
  { value: 'grandparent', label: 'Grand-parent' },
  { value: 'other', label: 'Autre' },
]
const FAMILY_CATEGORIES = [
  { value: 'cancer', label: 'Cancer' },
  { value: 'cardiovascular', label: 'Cardiovasculaire' },
  { value: 'diabetes', label: 'Diabète' },
  { value: 'neurodegenerative', label: 'Neurodégénératif' },
  { value: 'other', label: 'Autre' },
]
const DOC_TYPES = [
  { value: 'prescription', label: 'Ordonnance' },
  { value: 'lab_report', label: 'Analyse biologique' },
  { value: 'imaging', label: 'Imagerie' },
  { value: 'report', label: 'Compte-rendu' },
  { value: 'other', label: 'Autre' },
]

export default function MedicalRecordView() {
  const [profileInfo, setProfileInfo] = useState(null)

  useEffect(() => {
    santeApi.getProfile().then(setProfileInfo)
  }, [])

  return (
    <Box>
      <Box sx={{ ...cardSx, mb: 2 }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
          Votre dossier santé
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
          Un carnet de santé numérique, durable et exportable. Indépendant des
          capteurs — alimenté à la main ou par extraction de documents.
          {profileInfo?.age != null && ` Profil : ${profileInfo.age} ans.`}
        </Typography>
      </Box>

      <CrudSection
        title="Antécédents personnels"
        api={santeApi.conditions}
        fields={[
          { name: 'label', label: 'Libellé' },
          { name: 'condition_type', label: 'Type', options: CONDITION_TYPES },
          { name: 'notes', label: 'Notes' },
        ]}
        describe={(it) => `${it.label} — ${it.condition_type}`}
      />
      <CrudSection
        title="Addictions & sevrage"
        api={santeApi.addictions}
        fields={[
          {
            name: 'substances',
            label: 'Addiction(s)',
            options: ADDICTIONS,
            multiple: true,
            helper: 'Sélectionnez une ou plusieurs addictions.',
          },
          { name: 'status', label: 'Statut', options: ADDICTION_STATUSES },
          {
            name: 'quit_date',
            label: "Date d'arrêt",
            type: 'date',
            helper: 'Date visée ou effective (une par addiction).',
          },
          { name: 'notes', label: 'Notes' },
        ]}
        expandPayloads={(form) => {
          const subs =
            Array.isArray(form.substances) && form.substances.length
              ? form.substances
              : ['other']
          return subs.map((substance) => ({
            substance,
            status: form.status || 'active',
            quit_date: form.quit_date || null,
            notes: form.notes || '',
          }))
        }}
        describe={(it) => {
          const base = `${labelOf(ADDICTIONS, it.substance)} — ${labelOf(
            ADDICTION_STATUSES,
            it.status,
          )}`
          if (!it.quit_date) return base
          const since =
            it.days_since_quit != null && it.days_since_quit >= 0
              ? ` · ${it.days_since_quit} j sans`
              : ''
          return `${base} (arrêt le ${it.quit_date}${since})`
        }}
      />
      <CrudSection
        title="Antécédents familiaux"
        api={santeApi.familyHistory}
        fields={[
          { name: 'relation', label: 'Lien de parenté', options: RELATIONS },
          { name: 'category', label: 'Catégorie', options: FAMILY_CATEGORIES },
          { name: 'label', label: 'Précision' },
        ]}
        describe={(it) => `${it.category} — ${it.relation}`}
      />
      <CrudSection
        title="Traitements en cours"
        api={santeApi.treatments}
        fields={[
          { name: 'name', label: 'Médicament / molécule' },
          { name: 'dosage', label: 'Dosage' },
          { name: 'frequency', label: 'Fréquence' },
          { name: 'reason', label: 'Motif' },
          { name: 'prescriber', label: 'Prescripteur' },
        ]}
        describe={(it) =>
          `${it.name} ${it.dosage || ''} — ${it.frequency || 'fréquence non précisée'}`
        }
      />
      <CrudSection
        title="Vaccinations"
        api={santeApi.vaccinations}
        fields={[
          { name: 'vaccine_name', label: 'Vaccin' },
          { name: 'dose_label', label: 'Dose' },
          { name: 'administered_on', label: 'Date', type: 'date' },
        ]}
        describe={(it) =>
          `${it.vaccine_name} ${it.dose_label || ''} ${
            it.administered_on ? `(${it.administered_on})` : ''
          }`
        }
      />
      <CrudSection
        title="Rendez-vous médicaux"
        api={santeApi.appointments}
        fields={[
          { name: 'title', label: 'Intitulé' },
          { name: 'practitioner', label: 'Praticien' },
          { name: 'specialty', label: 'Spécialité' },
          { name: 'scheduled_for', label: 'Date et heure', type: 'datetime-local' },
          { name: 'location', label: 'Lieu' },
        ]}
        describe={(it) =>
          `${it.title} — ${it.practitioner || ''} (${
            it.scheduled_for ? new Date(it.scheduled_for).toLocaleString('fr-FR') : ''
          })`
        }
      />
      <CrudSection
        title="Analyses biologiques"
        api={santeApi.labResults}
        fields={[
          { name: 'marker', label: 'Marqueur' },
          { name: 'value', label: 'Valeur', type: 'number' },
          { name: 'unit', label: 'Unité' },
          { name: 'reference_range', label: 'Plage de référence' },
          { name: 'measured_on', label: 'Date', type: 'date' },
        ]}
        describe={(it) =>
          `${it.marker} = ${it.value ?? '?'} ${it.unit || ''} ${
            it.reference_range ? `(réf. ${it.reference_range})` : ''
          }`
        }
      />
      <CrudSection
        title="Documents médicaux"
        api={santeApi.documents}
        fields={[
          { name: 'title', label: 'Titre du document' },
          { name: 'document_type', label: 'Type', options: DOC_TYPES },
        ]}
        describe={(it) => `${it.title} — ${it.document_type} [${it.ocr_status}]`}
        extra={(it, reload) => (
          <Button
            size="small"
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
            onClick={async () => {
              await santeApi.processDocument(it.id)
              reload()
            }}
            sx={{ color: HEALTH_ACCENT, textTransform: 'none', fontSize: 12, px: 0 }}
          >
            Lancer l’extraction OCR + IA
          </Button>
        )}
      />
    </Box>
  )
}
